import React, { useState, useEffect } from "react";
import {
  DealQuestionnaireQuestionSettings,
  Responses,
  Score,
  QuestionValueType,
  DealsQuestionType,
  QuestionResponse
} from "@/pages/deals/types";
import { cn } from "@/utils/utils";
import { useUpdateDealQuestionnaireMutation } from "@/pages/deals/hooks";
import SingleCheckbox from "@/components/ui/SingleCheckbox";
import { TextQuestionInput } from "./TextQuestions";
import Text from "@/components/ui/text";

const getResponseText = (
  existingResponse?: QuestionResponse | null
): string | undefined => {
  if (!existingResponse?.response?.value) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(existingResponse.response?.value as string);
    return parsed.text;
  } catch {
    return undefined;
  }
};

interface BaseQuestionProps {
  question: DealQuestionnaireQuestionSettings;
  questionnaireId: string;
  dealId: string;
  handleUpdateQuestionnaireScore: (newValue: Score | null) => void;
  localResponses: Responses;
  updateResponse: (
    questionId: string,
    response: QuestionResponse | null
  ) => void;
  onResponseError: () => void;
  className?: string;
}

const CheckboxWithTextQuestion: React.FC<BaseQuestionProps> = ({
  question,
  questionnaireId,
  dealId,
  handleUpdateQuestionnaireScore,
  localResponses,
  updateResponse,
  onResponseError,
  className
}) => {
  const updateQuestionnaire = useUpdateDealQuestionnaireMutation();
  const { id, questionText, responseType } = question;

  const [isChecked, setIsChecked] = useState(false);
  const [textValue, setTextValue] = useState("");

  useEffect(() => {
    const existingValue = localResponses[id]?.response?.value;
    if (existingValue) {
      try {
        if (typeof existingValue === "string") {
          const parsed = JSON.parse(existingValue);
          if (typeof parsed === "object" && parsed !== null) {
            setIsChecked(parsed.checked || false);
            setTextValue(parsed.text || "");
          }
        } else if (typeof existingValue === "boolean") {
          // Handle legacy boolean responses
          setIsChecked(existingValue);
          setTextValue("");
        }
      } catch {
        // If parsing fails, treat as boolean
        setIsChecked(Boolean(existingValue));
        setTextValue("");
      }
    }
  }, [localResponses, id]);

  const handleUpdateResponse = (checked: boolean, text: string) => {
    const existingResponse =
      localResponses[id] || ({} as QuestionResponse | null);
    const responseText = getResponseText(existingResponse);

    if (text === responseText) return;

    const responseValue: QuestionValueType = JSON.stringify({
      checked,
      text: text.trim()
    });

    const newResponse = {
      ...existingResponse,
      response: { value: responseValue },
      lastEditedBy: undefined
    };

    updateResponse(id, newResponse);

    updateQuestionnaire.mutate(
      {
        pathParams: { dealId, questionnaireId },
        data: {
          questions: [
            {
              questionId: id,
              ...newResponse
            }
          ]
        }
      },
      {
        onSuccess: (data) => {
          handleUpdateQuestionnaireScore(data.score);
        },
        onError: onResponseError
      }
    );
  };

  const handleCheckboxChange = (checked: boolean | "indeterminate") => {
    const isChecked = checked === true;
    setIsChecked(isChecked);
    if (!isChecked) {
      setTextValue("");
      handleUpdateResponse(isChecked, "");
    } else {
      handleUpdateResponse(isChecked, textValue);
    }
  };

  const handleTextChange = (text: string) => {
    setTextValue(text);
    handleUpdateResponse(isChecked, text);
  };

  return (
    <div key={id} className={cn("flex flex-col gap-2", className)}>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 mx-1.5">
          <SingleCheckbox
            id={`checkbox-${id}`}
            isChecked={isChecked}
            onCheckedChange={handleCheckboxChange}
            label={questionText}
          />
        </div>
        {isChecked && (
          <div className="flex flex-col gap-1 pl-11">
            <Text>{responseType?.textAreaQuestion}</Text>
            <TextQuestionInput
              value={textValue}
              onChange={handleTextChange}
              question={{
                ...question,
                responseType: {
                  ...question.responseType,
                  type: DealsQuestionType.TEXT
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckboxWithTextQuestion;
